import type { DataGridPremiumProps, GridApiPremium, GridRenderCellParams } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import type {
  TabularDataCell,
  TabularRow,
  TabularSubtotalLabelPosition,
} from "../../../../../../../../api/types/svTypes.ts";
import { getCellFromRow, getFormattedValueWithFallback } from "./dimension-column/buildDimensionColumnHelper.ts";

export type RowGroupingModelType = DataGridPremiumProps["rowGroupingModel"];
export type RowGroupingColumnModeType = DataGridPremiumProps["rowGroupingColumnMode"];

export const getFieldNameFromGroupingColumnByFieldGuid = (groupColumnName: string) => {
  const prefix = "__row_group_by_columns_group_";
  const suffix = "__";
  if (groupColumnName.startsWith(prefix) && groupColumnName.endsWith(suffix)) {
    return groupColumnName.slice(prefix.length, -suffix.length);
  }
  return undefined;
};

export const getFieldNameFromFooterByDepth = (rowGroupingModel: RowGroupingModelType, params: GridRenderCellParams) => {
  const depth = params.rowNode.depth;
  const index = depth - 1;
  return rowGroupingModel?.[index];
};

export const getSubtotalLabel = (
  cell: TabularDataCell,
  subtotalLabelFormatPrefix: string | undefined = "",
  subtotalLabelFormatSuffix: string | undefined = ""
) => {
  return `${subtotalLabelFormatPrefix} ${getFormattedValueWithFallback(cell)} ${subtotalLabelFormatSuffix}`.trim();
};

export const getCellForSubtotal = (
  apiRef: RefObject<GridApiPremium>,
  subtotalLabelPosition: TabularSubtotalLabelPosition | undefined,
  rowGroupingModel: RowGroupingModelType,
  fieldName: string | undefined,
  params: GridRenderCellParams<TabularRow, TabularDataCell>
) => {
  if (subtotalLabelPosition === "Leading") {
    const isFirstColumn = rowGroupingModel?.[0] === fieldName;
    if (!isFirstColumn) {
      return;
    }
  }
  if (subtotalLabelPosition === "Group") {
    const indexOfTargetField = rowGroupingModel?.findIndex((field) => field === fieldName);
    const shouldShowValue = params.rowNode.depth - 1 === indexOfTargetField;
    if (!shouldShowValue) {
      return;
    }
  }
  if (subtotalLabelPosition === "Trailing") {
    const isLastColumn = rowGroupingModel?.[rowGroupingModel.length - 1] === fieldName;
    if (!isLastColumn) {
      return;
    }
  }

  const parent = apiRef.current?.getRow<TabularRow>(params.rowNode.parent ?? "");
  const row = parent?.groupField
    ? parent
    : apiRef.current?.getRow<TabularRow>(
        apiRef.current?.getRowGroupChildren({
          groupId: params.rowNode.parent ?? "",
        })?.[0] ?? ""
      );

  if (!row) {
    return;
  }
  const targetField = rowGroupingModel?.[params.rowNode.depth - 1];
  if (!targetField) {
    return;
  }
  return getCellFromRow(row, targetField);
};
